/**
 * BRAND NAME TRUTH — where a real brand name lives, and when it may be printed.
 *
 * THE DEFECT THIS REPLACES. `buildBrandMarkLaw` used to read its `brandName`
 * from `lockCtx.effectiveKit?.name` — i.e. `brand_kits.name` — and emit
 * "THE ONLY BRAND NAME PERMITTED anywhere in this image is exactly X", which
 * authorises the model to print X. But **`brand_kits.name` is a RECORD LABEL,
 * not a brand.** `src/hooks/useBrandKits.ts` creates every kit as
 * `'New brand kit'`. Proven consequences, all with no text requested:
 *   • a ceramic dripper engraved "Old Money — Mora"  (that kit's name is a LOOK)
 *   • a paper bag reading "New brand kit"            (the app's own default)
 *   • a bag reading "Audit Test Value / 2026-05-29"  (a QA record name + a DATE)
 *
 * WHAT PRODUCTION ACTUALLY HOLDS (queried 2026-07-31, project apegsjpotzvxumdedwty,
 * 13 kits across 8 users). Every candidate source was checked:
 *
 * | source                                     | verdict                                                          |
 * | ------------------------------------------ | ---------------------------------------------------------------- |
 * | `brand_kits.name`                          | RECORD LABEL. 8/13 are placeholders the app or QA wrote:          |
 * |                                            | 'My Brand Kit' ×4, 'New brand kit', 'Audit Test Value 2026-…',    |
 * |                                            | 'Smoothie King Greenville (TEST)', '…Corporate (TEST)'.           |
 * |                                            | And the kit literally NAMED 'Mora' has guidelines brand           |
 * |                                            | 'Best T Shirts Co.' — label and brand disagree outright.          |
 * | `user_brand_voices.profile_name`           | ALSO a record label — real rows read 'Casual Brand Voice', 'xyz'. |
 * | `user_profiles.company_name`               | User-typed at onboarding. 4/8 users populated (Mora, Yonex,       |
 * |                                            | Aera Studio, Experiment Beauty). Best structured candidate.       |
 * | `brand_kits.brand_guidelines->brand->name` | Richest, but CONTAMINATED: one row reads 'New Brand Kit Co.' —    |
 * |                                            | the app's own placeholder laundered into a brand by the           |
 * |                                            | guidelines generator, and 'Bloom Botanicals' sits on the          |
 * |                                            | 'Audit Test Value' kit.                                           |
 * | `…website_scan_data->brandName`            | Present on 1 of 4 scans. A corroborator, not a source.            |
 * | `brand_guidelines` (table)                 | DOES NOT EXIST — it is a jsonb column, not a table.               |
 *
 * **THE CONCLUSION THAT MATTERS: there is no clean source.** Every column that
 * looks like a brand name has at least one row where it is not one, because
 * each was seeded from the one before it. So the fix cannot be "pick a better
 * column" — picking any column keeps the same broken shape: *data presence
 * granting permission to print*.
 *
 * THE INVERSION. A brand name is printed ONLY when the merchant has positively
 * confirmed they want it rendered into images — `brand_kits.render_brand_name`
 * (the string they confirmed) plus `render_brand_name_confirmed_at` (that they
 * confirmed it). Absence, placeholder, or ANY doubt ⇒ every surface unmarked.
 * Unmarked is always safe; a wrong name never is.
 *
 * The other functions here exist to serve that confirmation step, not to bypass
 * it: `pickBrandNameCandidate` proposes what to pre-fill the confirm field with,
 * and it can never permit anything on its own.
 */

/**
 * Strings the APP ITSELF writes, or that QA/seed scripts wrote. Matched as
 * substrings after normalisation, so derivatives ('New Brand Kit Co.',
 * 'My Brand Kit (copy)') are caught too — that derivative is real: prod row
 * `f19e53d3` has kit name 'New brand kit' and guidelines brand 'New Brand Kit Co.'.
 */
const APP_DEFAULT_FRAGMENTS = [
  'new brand kit',
  'my brand kit',
  'brand kit',
  'brandkit',
  'untitled',
  'unnamed',
  'no name',
  'default brand',
  'new kit',
  'my brand',
  'your brand',
  'brand name',
  'company name',
  'enter your',
  'lorem ipsum',
];

/**
 * Non-brand vocabulary. Matched on WORD boundaries so real brands survive —
 * 'Protest Coffee' and 'Testa Rossa' must not be rejected by `test`. Doubt
 * still resolves to rejection everywhere else, but a word-boundary miss here
 * would reject working brands for a living, which is its own defect.
 */
const NON_BRAND_WORDS = [
  'test',
  'tests',
  'testing',
  'audit',
  'sample',
  'demo',
  'placeholder',
  'example',
  'dummy',
  'fixture',
  'seed',
  'staging',
  'sandbox',
  'qa',
  'tbd',
  'todo',
  'foo',
  'bar',
  'baz',
  'asdf',
  'xyz',
  'temp',
  'draft',
  'copy',
  'duplicate',
  'delete',
  'ignore',
];

/** ISO dates, slashed dates, month-name dates, and bare years. */
const DATE_PATTERNS = [
  /\b\d{4}-\d{1,2}-\d{1,2}\b/,
  /\b\d{1,2}[/.]\d{1,2}[/.]\d{2,4}\b/,
  /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s*\d{0,4}\b/i,
  /\b(?:19|20)\d{2}\b/,
];

/**
 * `true` when this string must never reach an image prompt as a brand.
 *
 * Deliberately one-way: a false positive costs an unmarked bag (correct and
 * intended per the law), a false negative costs a fabricated wordmark burned
 * into a customer's product photo. Every ambiguous case therefore returns
 * `true`.
 */
export function isPlaceholderBrandName(raw: string | null | undefined): boolean {
  if (typeof raw !== 'string') return true;

  const trimmed = raw.trim();
  if (trimmed.length === 0) return true;

  // Normalise the ways a record label decorates itself: '(TEST)', '— Mora',
  // 'v2', '#3', trailing punctuation. Unicode dashes included because the
  // real offending row is 'Old Money — Mora' (em dash).
  const normalized = trimmed
    .toLowerCase()
    .replace(/[‐-―]/g, '-')
    .replace(/[^\p{L}\p{N}\s/.:#()-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.length < 2) return true;
  // No letters at all — a number, an id, a date, a version string.
  if (!/\p{L}/u.test(normalized)) return true;

  for (const fragment of APP_DEFAULT_FRAGMENTS) {
    if (normalized.includes(fragment)) return true;
  }

  const words = normalized.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  for (const word of words) {
    if (NON_BRAND_WORDS.includes(word)) return true;
  }

  for (const pattern of DATE_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }

  // A record label that names an internal artefact rather than a business.
  // `#\d+` is matched without a leading `\b` on purpose: '#' is a non-word
  // character, so `\b#` only matches after a letter and 'Brand #3' slipped past.
  if (/\b(?:v\d+|rev\d+|\d{4,})\b/i.test(normalized) || /#\d+/.test(normalized)) return true;
  // Duplicated-substring artefacts — prod really contains
  // 'Audit Test Value 2026-0Audit Test Value 2026-05-295-29'.
  if (trimmed.length > 24) {
    const half = trimmed.slice(0, Math.floor(trimmed.length / 2)).trim();
    if (half.length >= 6 && trimmed.slice(half.length).includes(half.slice(0, 6))) return true;
  }
  // Absurd length for a brand — a sentence, a description, or a pasted blob.
  if (trimmed.length > 48) return true;
  if (words.length > 6) return true;

  return false;
}

/** Where a proposed name came from, so the confirm UI can say so honestly. */
export type BrandNameCandidateSource =
  'brand_guidelines' | 'user_profile_company' | 'website_scan' | 'brand_kit_name';

export interface BrandNameCandidateSources {
  /** `brand_kits.brand_guidelines` jsonb. */
  brandGuidelines?: Record<string, unknown> | null;
  /** `user_profiles.company_name`. */
  companyName?: string | null;
  /** `user_brand_voices.website_scan_data->>'brandName'`. */
  websiteScanBrandName?: string | null;
  /** `brand_kits.name` — last resort, and usually a record label. */
  brandKitName?: string | null;
}

export interface BrandNameCandidate {
  name: string;
  source: BrandNameCandidateSource;
  /** How many independent sources agree on this string (case-insensitive). */
  corroboration: number;
}

function guidelinesBrandName(
  guidelines: Record<string, unknown> | null | undefined,
): string | null {
  if (!guidelines || typeof guidelines !== 'object') return null;
  const brand = (guidelines as { brand?: unknown }).brand;
  if (!brand || typeof brand !== 'object') return null;
  const name = (brand as { name?: unknown }).name;
  return typeof name === 'string' ? name : null;
}

/**
 * The best GUESS at this merchant's real brand name, for pre-filling the
 * confirmation field. **This is not permission and cannot become permission** —
 * only `brand_kits.render_brand_name` + `render_brand_name_confirmed_at` are,
 * and only the user writes those.
 *
 * Ordered by how close the source sits to a human stating their own business
 * name, with every candidate filtered through `isPlaceholderBrandName` first —
 * which is why 'New Brand Kit Co.' (a real prod row) proposes nothing at all.
 */
export function pickBrandNameCandidate(
  sources: BrandNameCandidateSources,
): BrandNameCandidate | null {
  const ordered: Array<[BrandNameCandidateSource, string | null | undefined]> = [
    ['brand_guidelines', guidelinesBrandName(sources.brandGuidelines)],
    ['user_profile_company', sources.companyName],
    ['website_scan', sources.websiteScanBrandName],
    ['brand_kit_name', sources.brandKitName],
  ];

  const usable = ordered.filter(
    (entry): entry is [BrandNameCandidateSource, string] =>
      typeof entry[1] === 'string' && !isPlaceholderBrandName(entry[1]),
  );
  if (usable.length === 0) return null;

  const [source, name] = usable[0];
  const key = name.trim().toLowerCase();
  const corroboration = usable.filter(([, other]) => other.trim().toLowerCase() === key).length;
  return { name: name.trim(), source, corroboration };
}

export interface RenderBrandNameInput {
  /** `brand_kits.render_brand_name` — the string the merchant confirmed. */
  renderBrandName?: string | null;
  /** `brand_kits.render_brand_name_confirmed_at` — that they confirmed it. */
  renderBrandNameConfirmedAt?: string | null;
}

export type RenderBrandNameReason =
  'consented' | 'no-consent-recorded' | 'consent-incomplete' | 'consented-value-is-placeholder';

export interface RenderBrandNameDecision {
  /** The ONLY string any prompt may be told it is allowed to print. */
  permittedBrandName: string | null;
  reason: RenderBrandNameReason;
}

/**
 * THE INVERTED DEFAULT. Returns a printable name only when BOTH consent columns
 * are populated and the confirmed string itself survives placeholder detection
 * (a backstop: a merchant can paste anything into a text field, and 'New brand
 * kit' typed by hand is exactly as wrong as 'New brand kit' written by the app).
 *
 * Everything else — no row, no consent, half-written consent, a placeholder —
 * returns `null`, which the brand-mark law renders as every surface unmarked.
 */
export function resolveRenderBrandName(input: RenderBrandNameInput): RenderBrandNameDecision {
  const value = typeof input.renderBrandName === 'string' ? input.renderBrandName.trim() : '';
  const confirmedAt =
    typeof input.renderBrandNameConfirmedAt === 'string'
      ? input.renderBrandNameConfirmedAt.trim()
      : '';

  if (!value && !confirmedAt) return { permittedBrandName: null, reason: 'no-consent-recorded' };
  if (!value || !confirmedAt) return { permittedBrandName: null, reason: 'consent-incomplete' };
  if (isPlaceholderBrandName(value)) {
    return { permittedBrandName: null, reason: 'consented-value-is-placeholder' };
  }
  return { permittedBrandName: value, reason: 'consented' };
}
